import { ShowIf } from '../../components/ShowIf';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { LandSize } from '../components/LandSize';
import { SiteBuiltOrManufactured } from '../components/SiteBuiltOrManufactored';
import { CurrentAsIsValue } from '../components/CurrentAsIsValue';
import { CurrentDebt } from '../components/CurrentDebt';
import { DayOfWorkCompleted } from '../components/DayOfWorkCompleted';
import { HasWorkBeenCompleted } from '../components/HasWorkBeenCompleted';
import { TargetLoanRequest } from '../components/TargetLoanRequest';
import { StructureAndUnits } from '../components/StructureAndUnits/StructureAndUnits';
import { PreferredTerm } from './components/PreferredTerm';
import { SurveyFile } from '../../components/FileOrFormQuestions/questions/SurveyFile';
import { AnyWorkToBeCompleted } from './components/AnyWorkToBeCompleted/AnyWorkToBeCompleted';
import { ProofOfOccupancy } from './components/ProofOfOccupancy/ProofOfOccupancy';
import { usePropertyStructureType } from '../../../queries/PropertyFields/TopLevelFields';
import { usePropertyContactIsPrimary } from '../../../queries/PropertyFields/NonRentalContactFields';
import { HoaDues } from '../components/HoaDues';
import { PropertyDevelopmentInfo } from './components/PropertyDevelopmentInfo/PropertyDevelopmentInfo';
import { OnsitePropertyContact } from '../components/OnsitePropertyContact';
import { OnsitePropertyContactInformation } from '../components/OnsitePropertyContactInformation/OnsitePropertyContactInformation';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';
import { ExitInformation } from './components/ExitInformation/ExitInformation';
import { DropDownBoxComponentSpacing } from '@apply/components';
import { DropDownBoxLabelSize } from '@apply/components/src/components/controls/DropDownBox';
import { PreferredInterestPayments } from './components/PreferredInterestPayments';
import { usePropertyAcquisitionDate, usePropertyDayOfWorkCompleted } from '../../../queries/PropertyFields/NonRentalCoreFields';
import { useBorrowerLoanSubType } from '../../../queries/BorrowerFields/CoreFields';
import { LoanSubType } from '@apply/constants';
import { isDateInFuture } from '@apply/ui';
import { useState } from 'react';

export interface BridgeProps {
    propertyGuid: string;
    isReview?: boolean;
}

export const Bridge: React.FC<BridgeProps> = ({ propertyGuid, isReview = false }) => {
    const [structureType] = usePropertyStructureType(propertyGuid);
    const [contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);
    const [acquisitionDate] = usePropertyAcquisitionDate(propertyGuid);
    const [loanSubType] = useBorrowerLoanSubType();
    const [dayOfWorkCompleted, set_dayOfWorkCompleted] = usePropertyDayOfWorkCompleted(propertyGuid);
    const [hasWorkBeenCompleted, set_hasWorkBeenCompleted] = useState<boolean | undefined>(undefined);

    const isAcquisitionBridge = loanSubType === LoanSubType.AcquisitionBridge;
    const isInventoryLoan = loanSubType === LoanSubType.InventoryLoan;
    const showCurrentDebt = !isAcquisitionBridge || (!!acquisitionDate && !isDateInFuture(acquisitionDate));
    // Page-local answer wins once the user has clicked; otherwise infer from the saved date.
    // Property data loads asynchronously, so a useState initializer would run before the date arrives.
    const workCompleted = hasWorkBeenCompleted ?? !!dayOfWorkCompleted;

    const onHasWorkBeenCompletedChange = (value: boolean) => {
        set_hasWorkBeenCompleted(value);
        if (!value) {
            set_dayOfWorkCompleted(null);
        }
    };

    return (
        <>
            <StructureAndUnits
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <PropertyAddress
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Scenario</div>
            </ShowIf>
            <PropertyDevelopmentInfo
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <CurrentAsIsValue
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={showCurrentDebt}>
                <CurrentDebt
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={isInventoryLoan}>
                <HasWorkBeenCompleted
                    value={workCompleted}
                    onChange={onHasWorkBeenCompletedChange}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={isInventoryLoan && workCompleted}>
                <DayOfWorkCompleted
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <LandSize
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Improvements</div>
            </ShowIf>
            <SiteBuiltOrManufactured
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
                componentSizing={DropDownBoxComponentSpacing.LargeSpacing}
                labelSize={DropDownBoxLabelSize.Medium}
            />
            <ProofOfOccupancy
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <OnsitePropertyContact
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={contactIsPrimary === false}>
                <OnsitePropertyContactInformation
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <AnyWorkToBeCompleted
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <HoaDues
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Files</div>
            </ShowIf>
            <SurveyFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Exit</div>
            </ShowIf>
            <ExitInformation
                structureType={structureType}
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Request</div>
            </ShowIf>
            <PreferredTerm
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <PreferredInterestPayments
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <TargetLoanRequest
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
        </>
    );
};
